import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { freeCancellationHours, freeCancellationCopy } from "./card-policy";

import { requireSupabaseAuth } from "@/lib/require-auth";
import {
  bokunGet,
  bokunPost,
  clearBokunCache,
  BokunError,
  getBokunMetrics,
  resetBokunMetrics,
} from "./bokun-core.server";
import { recordOpsEvent } from "./ops-log.server";

async function ensureAdmin(userId: string, supabase: any) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .in("role", ["admin", "owner"])
    .limit(1);
  if (error) throw new Error(error.message);
  if (!data || data.length === 0) throw new Error("No tienes permisos de administrador.");
}

// --- Public DTOs (serializable) --------------------------------------------

export type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

export interface BokunProductSummary {
  cardCancellationPolicy?: string | null;
  freeCancellationHours?: number | null;
  pricingUnit?: "person" | "group" | "vessel" | null;
  id: string;
  slug: string | null;
  title: string;
  summary: string | null;
  description: string | null;
  images: string[];
  priceFrom: number | null;
  currency: string | null;
  durationMinutes: number | null;
  location: string | null;
  categories: string[];
  available: boolean;
  bookingType: string | null;
  capacityType: string | null;
  productCode: string | null;
  externalId: string | null;
  rating: number | null;
  reviewCount: number | null;
  instantConfirmation: boolean | null;
  vendorTitle: string | null;
}

export interface BokunPricingCategory {
  id: string;
  title: string;
  minAge: number | null;
  maxAge: number | null;
  ticketCategory: string | null;
  /** Personas que cubre una unidad (p. ej. 10 en una lancha privada). */
  groupSize?: number | null;
}

export interface BokunExtra {
  id: string;
  title: string;
  description: string | null;
  price: number | null;
  currency: string | null;
}

export interface BokunPickupOption {
  id: string;
  title: string;
  address: string | null;
}

export interface BokunItineraryItem {
  index: number;
  title: string;
  body: string | null;
}

export interface BokunStartPoint {
  id: string;
  title: string;
  address: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface BokunStartTime {
  id: string;
  hour: number;
  minute: number;
  label: string | null;
}

export interface BokunVendor {
  id: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
}

export interface BokunCancellationPolicy {
  title: string | null;
  summary: string;
}

export interface BokunMediaPhoto {
  url: string;
  alt: string | null;
  caption: string | null;
}

export interface BokunContact {
  email: string | null;
  phone: string | null;
  website: string | null;
}

export interface BokunVoucherInfo {
  type: string | null;
  redemptionMethod: string | null;
  printed: boolean | null;
}

export interface BokunProductDetail extends BokunProductSummary {
  highlights: string[];
  included: string[];
  excluded: string[];
  meetingType: string | null;
  ticketPerPerson: boolean | null;
  pricingCategories: BokunPricingCategory[];
  extras: BokunExtra[];
  pickupOptions: BokunPickupOption[];
  openingHours: Json;
  durationText: string | null;
  difficultyLevel: string | null;
  minAge: number | null;
  itinerary: BokunItineraryItem[];
  requirements: string | null;
  importantInfo: string | null;
  startPoints: BokunStartPoint[];
  startTimes: BokunStartTime[];
  vendor: BokunVendor | null;
  cancellationPolicy: BokunCancellationPolicy | null;
  bokunBookingUrl: string | null;
  guideLanguages: string[];
  audioLanguages: string[];
  subtitleLanguages: string[];
  restrictions: string | null;
  dressCode: string | null;
  physicalCondition: string | null;
  accessibility: string | null;
  voucherInfo: BokunVoucherInfo | null;
  bookingCutoffHours: number | null;
  minBookable: number | null;
  maxBookable: number | null;
  productGroups: string[];
  photos: BokunMediaPhoto[];
  contact: BokunContact | null;
  externalUrl: string | null;
  keywords: string[];
}

export interface BokunAvailabilityDay {
  date: string;
  startTime: string | null;
  startTimeId: number | null;
  available: boolean;
  soldOut: boolean;
  availabilityCount: number | null;
  pickupAvailabilityCount: number | null;
  minParticipants: number | null;
  minParticipantsToBookNow: number | null;
  price: number | null;
  currency: string | null;
  rates: Json;
  pricesByRate: Json;
}

export interface BokunPriceListEntry {
  pricingCategoryId: string;
  pricingCategoryTitle: string | null;
  ticketCategory: string | null;
  price: number | null;
  currency: string | null;
  rateId: string | null;
  rateTitle: string | null;
}

export interface BokunPickupPlace {
  id: string;
  title: string;
  address: string | null;
  customPickupAllowed: boolean;
}

// --- Generic helpers -------------------------------------------------------

type AnyRecord = Record<string, unknown>;

function pickString(o: AnyRecord, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
}

function pickNumber(o: AnyRecord, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

function pickBool(o: AnyRecord, ...keys: string[]): boolean | null {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "boolean") return v;
  }
  return null;
}

// Tries every known Bokun image collection field and every per-photo URL key.
// Preserves Bokun's order. Dedupes by final URL.
const IMAGE_LIST_KEYS = [
  "photos",
  "images",
  "media",
  "imageUrls",
  "gallery",
  "pictures",
  "imageGallery",
  "productImages",
  "additionalPhotos",
] as const;

const PHOTO_URL_KEYS = [
  "originalUrl",
  "url",
  "src",
  "href",
  "derivedUrl",
  "largeUrl",
  "mediumUrl",
  "smallUrl",
  "thumbnailUrl",
  "thumbUrl",
] as const;

function urlFromPhoto(p: unknown): string | null {
  if (typeof p === "string") return p.trim() || null;
  if (!p || typeof p !== "object") return null;
  const r = p as AnyRecord;
  for (const k of PHOTO_URL_KEYS) {
    const v = r[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  // Nested shapes: { image: {...} } | { photo: {...} } | { file: {...} }
  for (const k of ["image", "photo", "file", "asset"]) {
    const v = r[k];
    if (v && typeof v === "object") {
      const nested = urlFromPhoto(v);
      if (nested) return nested;
    }
  }
  return null;
}

function extractImages(raw: AnyRecord): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (u: string | null) => {
    if (u && !seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  };
  for (const key of IMAGE_LIST_KEYS) {
    const list = raw[key];
    if (Array.isArray(list)) {
      for (const p of list) push(urlFromPhoto(p));
    }
  }
  // keyPhoto / coverPhoto / mainPhoto as single objects or strings
  for (const k of ["keyPhoto", "coverPhoto", "mainPhoto", "primaryPhoto", "featuredImage"]) {
    if (raw[k]) push(urlFromPhoto(raw[k]));
  }
  return out;
}

function extractCategories(raw: AnyRecord): string[] {
  const cats: string[] = [];
  const lists: unknown[] = [raw.categories, raw.productCategories, raw.tags].filter(Array.isArray);
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const c of list) {
      if (typeof c === "string") cats.push(c);
      else if (c && typeof c === "object") {
        const r = c as AnyRecord;
        const name = pickString(r, "name", "title", "label");
        if (name) cats.push(name);
      }
    }
  }
  return Array.from(new Set(cats));
}

function extractLocation(raw: AnyRecord): string | null {
  const direct = pickString(raw, "location", "city", "place");
  if (direct) return direct;
  const start = raw.startPoint || raw.googlePlace;
  if (start && typeof start === "object") {
    const r = start as AnyRecord;
    return pickString(r, "title", "name", "address", "formattedAddress", "city");
  }
  if (Array.isArray(raw.startPoints) && raw.startPoints.length) {
    const r = raw.startPoints[0] as AnyRecord;
    return pickString(r, "title", "name", "address");
  }
  return null;
}

function extractPrice(raw: AnyRecord): { price: number | null; currency: string | null } {
  let currency = pickString(raw, "currency", "defaultCurrency");
  // Fall back to the activity's price catalog default currency.
  if (!currency && Array.isArray(raw.activityPriceCatalogs)) {
    for (const c of raw.activityPriceCatalogs as AnyRecord[]) {
      const cat = ((c as AnyRecord)?.catalog ?? {}) as AnyRecord;
      const cur = pickString(cat, "defaultCurrency");
      if (cur) {
        currency = cur;
        break;
      }
    }
  }
  const direct = pickNumber(raw, "priceFrom", "nextDefaultPrice", "price", "originalDefaultPrice");
  if (direct !== null && direct > 0) return { price: direct, currency };
  const money = raw.nextDefaultPriceMoney as AnyRecord | undefined;
  if (money) {
    const amt = pickNumber(money, "amount", "value");
    if (amt !== null && amt > 0)
      return { price: amt, currency: pickString(money, "currency") ?? currency };
  }
  const pricing = raw.pricing as AnyRecord | undefined;
  if (pricing) {
    return {
      price: pickNumber(pricing, "priceFrom", "minPrice", "amount"),
      currency: currency ?? pickString(pricing, "currency"),
    };
  }
  return { price: null, currency };
}

function extractDuration(raw: AnyRecord): number | null {
  const minutes = pickNumber(raw, "durationMinutes", "durationInMinutes");
  const hours = pickNumber(raw, "durationHours");
  // Bokun often sends durationMinutes=0 when the duration is set in whole hours.
  if (minutes !== null && minutes > 0) return minutes + (hours && hours > 0 ? hours * 60 : 0);
  if (hours !== null && hours > 0) return hours * 60;
  const dur = raw.duration as AnyRecord | undefined;
  if (dur && typeof dur === "object") {
    const m = pickNumber(dur, "minutes");
    const h = pickNumber(dur, "hours");
    if ((m !== null && m > 0) || (h !== null && h > 0)) return (h ?? 0) * 60 + (m ?? 0);
  }
  if (typeof raw.duration === "number" && raw.duration > 0) return Math.round(raw.duration * 60);
  return null;
}

function arrStrings(raw: AnyRecord, key: string): string[] {
  const v = raw[key];
  if (typeof v === "string") {
    // Bokun returns some "list" fields as a single HTML/text block — split lines.
    return v
      .replace(/<\s*br\s*\/?\s*>/gi, "\n")
      .replace(/<\/(p|li|div)>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => {
      if (typeof x === "string") return x;
      if (x && typeof x === "object") {
        const r = x as AnyRecord;
        return pickString(r, "title", "name", "text", "description") ?? "";
      }
      return "";
    })
    .filter(Boolean);
}

function humanizeCode(code: string): string {
  return code
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function stripHtml(s: string | null): string | null {
  if (!s) return s;
  return (
    s
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim() || null
  );
}

function toSummary(raw: AnyRecord): BokunProductSummary {
  const { price, currency } = extractPrice(raw);
  const tripadvisor = raw.tripadvisorReview as AnyRecord | undefined;
  return {
    cardCancellationPolicy: freeCancellationCopy(freeCancellationHours(raw.cancellationPolicy), "es"),
    freeCancellationHours: freeCancellationHours(raw.cancellationPolicy),
    pricingUnit: raw.ticketPerPerson === true ? "person" : raw.ticketPerPerson === false ? "group" : null,
    id: String(raw.id ?? raw.productId ?? raw.externalId ?? ""),
    slug: pickString(raw, "slug", "seoSlug"),
    title: pickString(raw, "title", "name") ?? "Untitled",
    summary: pickString(raw, "excerpt", "summary", "shortDescription"),
    description: pickString(raw, "description", "longDescription"),
    images: extractImages(raw),
    // Product defaults are base prices, not customer invoice totals.
    priceFrom: null,
    currency,
    durationMinutes: extractDuration(raw),
    location: extractLocation(raw),
    categories: extractCategories(raw),
    available: raw.bookable !== false && raw.active !== false,
    bookingType: pickString(raw, "bookingType"),
    capacityType: pickString(raw, "capacityType"),
    productCode: pickString(raw, "productCode", "code", "externalId"),
    externalId: pickString(raw, "externalId"),
    rating:
      pickNumber(raw, "reviewRating", "rating", "averageRating") ??
      (tripadvisor ? pickNumber(tripadvisor, "rating") : null),
    reviewCount:
      pickNumber(raw, "reviewCount", "numberOfReviews") ??
      (tripadvisor ? pickNumber(tripadvisor, "numReviews") : null),
    instantConfirmation: pickBool(raw, "instantConfirmation"),
    vendorTitle: (() => {
      const v = raw.vendor as AnyRecord | undefined;
      return v ? pickString(v, "title", "name") : null;
    })(),
  };
}

function toPricingCategories(raw: AnyRecord): BokunPricingCategory[] {
  const list = raw.pricingCategories;
  if (!Array.isArray(list)) return [];
  return list.map((c) => {
    const r = c as AnyRecord;
    return {
      id: String(r.id ?? r.pricingCategoryId ?? ""),
      title: pickString(r, "title", "name") ?? "",
      minAge: pickNumber(r, "minAge"),
      maxAge: pickNumber(r, "maxAge"),
      ticketCategory: pickString(r, "ticketCategory"),
      groupSize: pickNumber(r, "groupSize") || pickNumber(r, "occupancy") || null,
    };
  });
}

function toExtras(raw: AnyRecord): BokunExtra[] {
  const list = (raw.bookableExtras ?? raw.extras) as unknown;
  if (!Array.isArray(list)) return [];
  return list.map((c) => {
    const r = c as AnyRecord;
    return {
      id: String(r.id ?? ""),
      title: pickString(r, "title", "name") ?? "",
      description: pickString(r, "description", "summary"),
      price: pickNumber(r, "price", "defaultPrice"),
      currency: pickString(r, "currency"),
    };
  });
}

function toPickupOptions(raw: AnyRecord): BokunPickupOption[] {
  // Bokun returns `pickupPlaceGroups: [{ places: [...] }]` (or empty when no pickup service)
  const groups = raw.pickupPlaceGroups;
  const flat: AnyRecord[] = [];
  if (Array.isArray(groups)) {
    for (const g of groups) {
      const r = g as AnyRecord;
      if (Array.isArray(r.places)) flat.push(...(r.places as AnyRecord[]));
      else flat.push(r);
    }
  }
  const direct = (raw.pickupPlaces ?? raw.pickupOptions) as unknown;
  if (Array.isArray(direct)) flat.push(...(direct as AnyRecord[]));
  if (flat.length === 0) return [];
  return flat.map((r) => ({
    id: String(r.id ?? ""),
    title: pickString(r, "title", "name") ?? "",
    address: pickString(r, "address", "formattedAddress"),
  }));
}

function toItinerary(raw: AnyRecord): BokunItineraryItem[] {
  const list = raw.agendaItems;
  if (!Array.isArray(list)) return [];
  return list
    .map((c) => {
      const r = c as AnyRecord;
      return {
        index: typeof r.index === "number" ? r.index : 0,
        title: pickString(r, "title") ?? "",
        body: pickString(r, "body", "excerpt"),
      };
    })
    .sort((a, b) => a.index - b.index);
}

function toStartPoints(raw: AnyRecord): BokunStartPoint[] {
  const list = raw.startPoints;
  if (!Array.isArray(list)) return [];
  return list.map((c) => {
    const r = c as AnyRecord;
    const addr = (r.address as AnyRecord | undefined) ?? {};
    const geo = (addr.geoPoint as AnyRecord | undefined) ?? {};
    return {
      id: String(r.id ?? ""),
      title: pickString(r, "title", "name") ?? "",
      address: pickString(addr, "addressLine1", "formattedAddress"),
      city: pickString(addr, "city"),
      latitude: pickNumber(geo, "latitude"),
      longitude: pickNumber(geo, "longitude"),
    };
  });
}

function toStartTimes(raw: AnyRecord): BokunStartTime[] {
  const list = raw.startTimes;
  if (!Array.isArray(list)) return [];
  return list.map((c) => {
    const r = c as AnyRecord;
    return {
      id: String(r.id ?? ""),
      hour: pickNumber(r, "hour") ?? 0,
      minute: pickNumber(r, "minute") ?? 0,
      label: pickString(r, "externalLabel", "label"),
    };
  });
}

function toCancellationPolicy(raw: AnyRecord): BokunCancellationPolicy | null {
  const p = raw.cancellationPolicy as AnyRecord | undefined;
  if (!p) return null;
  const rules = Array.isArray(p.penaltyRules) ? (p.penaltyRules as AnyRecord[]) : [];
  const parts = rules.map((r) => {
    const hours = pickNumber(r, "cutoffHours") ?? 0;
    const charge = pickNumber(r, "charge") ?? 0;
    const type = pickString(r, "chargeType") ?? "percentage";
    const unit = type === "percentage" ? "%" : "";
    if (hours <= 0) return `${charge}${unit} de cargo`;
    if (hours >= 24 * 60)
      return `Sin cargo si cancelas con más de ${Math.round(hours / 24)} días de antelación.`;
    return `${charge}${unit} de cargo si cancelas con menos de ${hours} horas de antelación.`;
  });
  return { title: pickString(p, "title"), summary: parts.join(" ") };
}

function toVendor(raw: AnyRecord): BokunVendor | null {
  const v = raw.vendor as AnyRecord | undefined;
  if (!v) return null;
  const contact = (v.contact as AnyRecord | undefined) ?? {};
  return {
    id: String(v.id ?? ""),
    title: pickString(v, "title", "name"),
    email: pickString(v, "email") ?? pickString(contact, "email"),
    phone: pickString(v, "phoneNumber", "phone") ?? pickString(contact, "phoneNumber", "phone"),
    website: pickString(v, "website") ?? pickString(contact, "website"),
  };
}

function toContact(raw: AnyRecord): BokunContact | null {
  const c = (raw.contact as AnyRecord | undefined) ?? {};
  const email = pickString(c, "email") ?? pickString(raw, "email", "contactEmail");
  const phone =
    pickString(c, "phoneNumber", "phone") ?? pickString(raw, "phoneNumber", "contactPhone");
  const website = pickString(c, "website") ?? pickString(raw, "website");
  if (!email && !phone && !website) return null;
  return { email, phone, website };
}

function toVoucherInfo(raw: AnyRecord): BokunVoucherInfo | null {
  const type = pickString(raw, "voucherType");
  const redemption = pickString(raw, "redemptionMethod");
  const printed = pickBool(raw, "printedVouchersRequired");
  if (!type && !redemption && printed === null) return null;
  return { type, redemptionMethod: redemption, printed };
}

function toPhotos(raw: AnyRecord): BokunMediaPhoto[] {
  const out: BokunMediaPhoto[] = [];
  const seen = new Set<string>();
  for (const key of IMAGE_LIST_KEYS) {
    const list = raw[key];
    if (!Array.isArray(list)) continue;
    for (const p of list) {
      const url = urlFromPhoto(p);
      if (!url || seen.has(url)) continue;
      seen.add(url);
      const r = (p && typeof p === "object" ? p : {}) as AnyRecord;
      out.push({
        url,
        alt: pickString(r, "alternateText", "alt", "altText"),
        caption: pickString(r, "caption", "title", "description"),
      });
    }
  }
  for (const k of ["keyPhoto", "coverPhoto", "mainPhoto", "primaryPhoto", "featuredImage"]) {
    const url = urlFromPhoto(raw[k]);
    if (url && !seen.has(url)) {
      seen.add(url);
      out.push({ url, alt: null, caption: null });
    }
  }
  return out;
}

function langArr(raw: AnyRecord, ...keys: string[]): string[] {
  for (const k of keys) {
    const v = raw[k];
    if (Array.isArray(v)) {
      return v
        .map((x) =>
          typeof x === "string"
            ? x
            : ((x as AnyRecord)?.language ?? (x as AnyRecord)?.code ?? (x as AnyRecord)?.name),
        )
        .filter((x): x is string => typeof x === "string" && !!x);
    }
  }
  return [];
}

function buildBokunBookingUrl(raw: AnyRecord): string | null {
  const channelUuid = process.env.BOKUN_CHANNEL_UUID;
  const id = raw.id ?? raw.productId;
  if (!id) return null;
  if (channelUuid) {
    return `https://widgets.bokun.io/online-sales/${encodeURIComponent(channelUuid)}/experience/${encodeURIComponent(String(id))}`;
  }
  return null;
}

function toDetail(raw: AnyRecord): BokunProductDetail {
  const base = toSummary(raw);
  return {
    ...base,
    highlights: arrStrings(raw, "highlights"),
    included: arrStrings(raw, "included").length
      ? arrStrings(raw, "included")
      : arrStrings(raw, "includedItems").length
        ? arrStrings(raw, "includedItems")
        : arrStrings(raw, "inclusions").map(humanizeCode),
    excluded: arrStrings(raw, "excluded").length
      ? arrStrings(raw, "excluded")
      : arrStrings(raw, "excludedItems").length
        ? arrStrings(raw, "excludedItems")
        : arrStrings(raw, "exclusions").map(humanizeCode),
    meetingType: pickString(raw, "meetingType"),
    ticketPerPerson: pickBool(raw, "ticketPerPerson"),
    pricingCategories: toPricingCategories(raw),
    extras: toExtras(raw),
    pickupOptions: toPickupOptions(raw),
    openingHours: (raw.defaultOpeningHours ?? raw.openingHours ?? null) as Json,
    durationText: pickString(raw, "durationText"),
    difficultyLevel: pickString(raw, "difficultyLevel"),
    minAge: pickNumber(raw, "minAge"),
    itinerary: toItinerary(raw),
    requirements: stripHtml(pickString(raw, "requirements")),
    importantInfo: stripHtml(pickString(raw, "attention", "additionalInfo")),
    startPoints: toStartPoints(raw),
    startTimes: toStartTimes(raw),
    vendor: toVendor(raw),
    cancellationPolicy: toCancellationPolicy(raw),
    bokunBookingUrl: buildBokunBookingUrl(raw),
    // Bokun's activity payload only exposes a single `languages` array — surface it
    // as the guide languages. Audio/subtitle tracks are not exposed by the REST API.
    guideLanguages: langArr(raw, "guideLanguages", "languages"),
    audioLanguages: langArr(raw, "audioLanguages"),
    subtitleLanguages: langArr(raw, "subtitleLanguages"),
    // `knowBeforeYouGoItems` is the canonical restriction/notes list (enum codes).
    restrictions: (() => {
      const codes = arrStrings(raw, "knowBeforeYouGoItems");
      const text = stripHtml(pickString(raw, "restrictions", "restrictionsDescription"));
      const parts = [...codes.map(humanizeCode), text].filter(Boolean) as string[];
      return parts.length ? parts.join(" • ") : null;
    })(),
    // `dressCode` in Bokun is a boolean flag indicating a dress code applies.
    dressCode:
      typeof raw.dressCode === "string"
        ? (raw.dressCode as string)
        : raw.dressCode === true
          ? "Aplica código de vestimenta"
          : null,
    physicalCondition: pickString(raw, "physicalCondition", "physicalDifficulty"),
    accessibility: (() => {
      const types = arrStrings(raw, "supportedAccessibilityTypes").map(humanizeCode);
      const direct = pickString(raw, "accessibility", "accessibilityInfo");
      const parts = [...types, direct].filter(Boolean) as string[];
      return parts.length ? parts.join(" • ") : null;
    })(),
    voucherInfo: toVoucherInfo(raw),
    bookingCutoffHours: pickNumber(raw, "bookingCutoffHours", "cutoffHours"),
    minBookable: pickNumber(raw, "minParticipants", "minBookable"),
    maxBookable: pickNumber(raw, "maxParticipants", "maxBookable"),
    productGroups: (() => {
      const id = pickNumber(raw, "productGroupId");
      return id && id > 0 ? [String(id)] : arrStrings(raw, "productGroups");
    })(),

    photos: toPhotos(raw),
    contact: toContact(raw),
    externalUrl: pickString(raw, "externalUrl", "websiteUrl"),
    keywords: arrStrings(raw, "keywords"),
  };
}

// --- Server functions ------------------------------------------------------

// Returns ALL products by following pagination automatically.
// Optional `lang` is forwarded to Bokun so translatable fields (title, summary,
// description, highlights, etc.) come back in the requested language when
// available — otherwise Bokun falls back to the product default.
export const listProducts = createServerFn({ method: "GET" })
  .inputValidator(z.object({ lang: z.string().min(2).max(5).optional() }).optional())
  .handler(async ({ data }) => {
    const lang = data?.lang;
    const all: AnyRecord[] = [];
    // True when pagination ended early (deadline or a failed page), i.e. the
    // returned list may be missing bookable products. Callers must not cache
    // such a list as last-known-good.
    let partial = false;
    const pageSize = 100;
    const maxPages = 50; // hard cap (5000 products)
    // Request-level deadline: a slow Bokun must never stall a page render.
    // Once exceeded we return whatever pages we already have.
    // Budget must stay well under the SSR stream lifetime: this call is
    // awaited during page render on a cold cache, and translation work runs
    // after it. Exceeding it aborts the whole stream ("Stream lifetime exceeded").
    const OVERALL_BUDGET_MS = 12_000;
    const FIRST_PAGE_TIMEOUT_MS = 7_000;
    const NEXT_PAGE_TIMEOUT_MS = 5_000;
    const startedAt = Date.now();
    for (let page = 1; page <= maxPages; page++) {
      const remaining = OVERALL_BUDGET_MS - (Date.now() - startedAt);
      if (remaining <= 1_000) {
        console.warn("[bokun] listProducts budget exhausted", { page, collected: all.length });
        partial = true;
        break;
      }
      const isFirstPage = page === 1;
      let raw: unknown;
      try {
        raw = await bokunPost<unknown>(
          "/activity.json/search",
          { page, pageSize, forSale: true },
          lang ? { lang } : undefined,
          {
            timeoutMs: isFirstPage ? FIRST_PAGE_TIMEOUT_MS : NEXT_PAGE_TIMEOUT_MS,
            maxRetries: isFirstPage ? 1 : 0,
            // Retries of the first page may use most of the budget, but never
            // exceed it: the core client clamps each attempt to the deadline.
            deadlineAt: startedAt + OVERALL_BUDGET_MS,
          },
        );
      } catch (err) {
        // Partial catalog beats a failed page render.
        if (all.length > 0) {
          console.warn("[bokun] listProducts partial result", {
            page,
            collected: all.length,
            err: (err as Error).message,
          });
          partial = true;
          break;
        }
        if (err instanceof BokunError) {
          throw new Error(err.userMessage);
        }
        throw err;
      }
      const items = Array.isArray(raw)
        ? (raw as AnyRecord[])
        : Array.isArray((raw as AnyRecord)?.items)
          ? ((raw as AnyRecord).items as AnyRecord[])
          : Array.isArray((raw as AnyRecord)?.results)
            ? ((raw as AnyRecord).results as AnyRecord[])
            : [];

      all.push(...items);

      const totalHits = pickNumber((raw as AnyRecord) ?? {}, "totalHits", "total", "totalCount");
      if (items.length < pageSize) break;
      if (totalHits !== null && all.length >= totalHits) break;
    }
    return { products: all.map(toSummary), count: all.length, partial };
  });

/** Detalle interno (sin filtro de catálogo). Solo para código de servidor. */
export async function fetchBokunProduct(id: string, langIn?: string) {
  const raw = await bokunGet<AnyRecord>(
    `/activity.json/${encodeURIComponent(id)}`,
    langIn ? { lang: langIn } : undefined,
    5 * 60_000,
  );
  const product = toDetail(raw);
  const lang = (langIn ?? "").slice(0, 2).toLowerCase();
  product.cardCancellationPolicy = freeCancellationCopy(freeCancellationHours(raw.cancellationPolicy), lang || "es");
  if (lang && lang !== "es") {
    try {
      const { translateProductFields } = await import("./translate.server");
      await translateProductFields(product, lang);
    } catch (e) {
      console.warn("[bokun] product translation failed", e);
    }
  }
  return { product };
}

/** Respuesta pública: sin datos de contacto del proveedor ni del producto. */
function toPublicProduct(product: BokunProductDetail): BokunProductDetail {
  return {
    ...product,
    vendor: product.vendor
      ? { id: product.vendor.id, title: product.vendor.title, email: null, phone: null, website: null }
      : null,
    contact: null,
  };
}

async function guardPublicId(id: string) {
  const { assertPublicBokunId } = await import("./public-catalog-guard.server");
  await assertPublicBokunId(id);
}

export const getProduct = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      id: z.string().min(1),
      lang: z.string().min(2).max(5).optional(),
    }),
  )
  .handler(async ({ data }) => {
    await guardPublicId(data.id);
    const { product } = await fetchBokunProduct(data.id, data.lang);
    return { product: toPublicProduct(product) };
  });

/** Epoch en milisegundos (formato real de Bókun) → `YYYY-MM-DD` en UTC. */
function epochToIsoDate(value: unknown): string | null {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return new Date(n).toISOString().slice(0, 10);
}

export const getAvailability = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      id: z.string().min(1),
      start: z.string().optional(),
      end: z.string().optional(),
      currency: z.string().optional(),
    }),
  )
  .handler(async ({ data }) => {
    await guardPublicId(data.id);
    const today = new Date();
    const in30 = new Date();
    in30.setDate(today.getDate() + 30);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    const start = data.start ?? fmt(today);
    const end = data.end ?? fmt(in30);

    const raw = await bokunGet<unknown>(
      `/activity.json/${encodeURIComponent(data.id)}/availabilities`,
      { start, end, currency: data.currency ?? null },
      60_000,
    );
    const list = Array.isArray(raw) ? (raw as AnyRecord[]) : [];
    const days: BokunAvailabilityDay[] = list.map((r) => ({
      // Bókun devuelve `date` como epoch en milisegundos; solo algunas
      // respuestas traen texto. Se normaliza a `YYYY-MM-DD`.
      date: pickString(r, "date", "day") ?? epochToIsoDate(r.date) ?? "",
      startTime: pickString(r, "startTime"),
      startTimeId: pickNumber(r, "startTimeId"),
      available:
        r.unavailable !== true &&
        (r.availabilityCount === undefined || Number(r.availabilityCount) > 0),
      soldOut: r.soldOut === true,
      availabilityCount: pickNumber(r, "availabilityCount"),
      pickupAvailabilityCount: pickNumber(r, "pickupAvailabilityCount"),
      minParticipants: pickNumber(r, "minParticipants"),
      minParticipantsToBookNow: pickNumber(r, "minParticipantsToBookNow"),
      price: pickNumber(r, "pricePerPerson", "price"),
      currency: pickString(r, "currency"),
      rates: (r.rates ?? null) as Json,
      pricesByRate: (r.pricesByRate ?? null) as Json,
    }));
    return { days };
  });

export const getPriceList = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().min(1) }))
  .handler(async ({ data }) => {
    await guardPublicId(data.id);
    const raw = await bokunGet<unknown>(
      `/activity.json/${encodeURIComponent(data.id)}/price-list`,
      undefined,
      5 * 60_000,
    );
    const rawObj = (raw ?? {}) as AnyRecord;
    const entries: BokunPriceListEntry[] = [];
    // Modern shape: { pricesByDateRange: [{ rates: [{ rateId, title, passengers: [{ pricingCategoryId, title, ticketCategory, price: { amount, currency } }] }] }] }
    const today = new Date().toISOString().slice(0, 10);
    const ranges = Array.isArray(rawObj.pricesByDateRange)
      ? (rawObj.pricesByDateRange as AnyRecord[])
      : [];
    // Prefer the range covering today, fall back to first.
    const range =
      ranges.find((r) => {
        const from = String(r.from ?? "");
        const to = String(r.to ?? "");
        return (!from || from <= today) && (!to || to >= today);
      }) ?? ranges[0];
    if (range) {
      const rates = Array.isArray(range.rates) ? (range.rates as AnyRecord[]) : [];
      // Pick a single rate (lowest priced or first) to avoid duplicate categories.
      // Aggregate by pricingCategoryId, taking the lowest price across rates.
      const byCat = new Map<string, BokunPriceListEntry>();
      for (const rate of rates) {
        const rateId = pickString(rate, "rateId", "id");
        const rateTitle = pickString(rate, "title", "name");
        const passengers = Array.isArray(rate.passengers) ? (rate.passengers as AnyRecord[]) : [];
        for (const p of passengers) {
          const id = String(p.pricingCategoryId ?? p.id ?? "");
          if (!id) continue;
          const priceObj = (p.price ?? {}) as AnyRecord;
          const amount =
            pickNumber(priceObj, "amount", "value") ?? pickNumber(p, "price", "amount");
          const currency =
            pickString(priceObj, "currency") ??
            pickString(p, "currency") ??
            pickString(rawObj, "defaultCurrency");
          const next: BokunPriceListEntry = {
            pricingCategoryId: id,
            pricingCategoryTitle: pickString(p, "title", "name", "pricingCategoryTitle"),
            ticketCategory: pickString(p, "ticketCategory"),
            price: amount,
            currency,
            rateId,
            rateTitle,
          };
          const prev = byCat.get(id);
          if (!prev || (prev.price ?? Infinity) > (next.price ?? Infinity)) {
            byCat.set(id, next);
          }
        }
      }
      entries.push(...byCat.values());
    }
    // Legacy fallback shapes: flat array or { prices: [...] }
    if (entries.length === 0) {
      const list = Array.isArray(raw)
        ? (raw as AnyRecord[])
        : Array.isArray(rawObj.prices)
          ? (rawObj.prices as AnyRecord[])
          : [];
      for (const r of list) {
        entries.push({
          pricingCategoryId: String(r.pricingCategoryId ?? r.id ?? ""),
          pricingCategoryTitle: pickString(r, "pricingCategoryTitle", "title"),
          ticketCategory: pickString(r, "ticketCategory"),
          price: pickNumber(r, "amount", "price"),
          currency: pickString(r, "currency"),
          rateId: pickString(r, "rateId"),
          rateTitle: pickString(r, "rateTitle"),
        });
      }
    }
    return { entries };
  });

export const getPickupPlaces = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().min(1) }))
  .handler(async ({ data }) => {
    await guardPublicId(data.id);
    const raw = await bokunGet<unknown>(
      `/activity.json/${encodeURIComponent(data.id)}/pickup-places`,
      undefined,
      5 * 60_000,
    );
    const list = Array.isArray(raw) ? (raw as AnyRecord[]) : [];
    const places: BokunPickupPlace[] = list.map((r) => ({
      id: String(r.id ?? ""),
      title: pickString(r, "title", "name") ?? "",
      address: pickString(r, "address", "formattedAddress"),
      customPickupAllowed: r.customPickupAllowed === true,
    }));
    return { places };
  });

export const refreshBokunCache = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.userId, context.supabase);
    const { runBokunResync, setLastResync } = await import("./bokun-sync.server");
    const stats = await runBokunResync({ triggeredBy: `admin:${context.userId}` });
    setLastResync(`admin:${context.userId}`, stats);
    const m = getBokunMetrics();
    // Keep the older admin UI shape (attempts + currencies) by extending stats.
    return {
      ...stats,
      availableCount: stats.productCount, // legacy field kept for UI compat
      currencies: [] as string[],
      priceRange: null as null | { min: number; max: number },
      attempts: m.attempts,
    };
  });
